import type { SyncedShift } from '@/types/api';

interface ShiftFormProps {
  formData: Partial<SyncedShift>;
  onChange: (data: Partial<SyncedShift>) => void;
}

// POS stores days as integer indices: 0=Sun, 1=Mon, 2=Tue, 3=Wed, 4=Thu, 5=Fri, 6=Sat
const DAYS = [
  { key: '0', label: 'Sun' },
  { key: '1', label: 'Mon' },
  { key: '2', label: 'Tue' },
  { key: '3', label: 'Wed' },
  { key: '4', label: 'Thu' },
  { key: '5', label: 'Fri' },
  { key: '6', label: 'Sat' },
];

export function ShiftForm({ formData, onChange }: ShiftFormProps) {
  const selectedDays = (formData.daysOfWeek || '').split(',').filter(Boolean);

  const handleDayToggle = (day: string) => {
    const updated = selectedDays.includes(day)
      ? selectedDays.filter((d) => d !== day)
      : [...selectedDays, day];
    onChange({ ...formData, daysOfWeek: updated.join(',') || undefined });
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
        <input
          type="text"
          value={formData.name || ''}
          onChange={(e) => onChange({ ...formData, name: e.target.value })}
          className="input w-full"
          required
        />
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Time *</label>
          <input
            type="time"
            value={formData.startTime || ''}
            onChange={(e) => onChange({ ...formData, startTime: e.target.value })}
            className="input w-full"
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Time *</label>
          <input
            type="time"
            value={formData.endTime || ''}
            onChange={(e) => onChange({ ...formData, endTime: e.target.value })}
            className="input w-full"
            required
          />
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Days of Week</label>
        <div className="flex flex-wrap gap-2">
          {DAYS.map(({ key, label }) => {
            const checked = selectedDays.includes(key);
            return (
              <label
                key={key}
                className={`inline-flex items-center px-3 py-1.5 rounded-md border text-sm cursor-pointer transition-colors ${
                  checked
                    ? 'bg-primary-100 border-primary-400 text-primary-800'
                    : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
                }`}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  onChange={() => handleDayToggle(key)}
                  className="sr-only"
                />
                {label}
              </label>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
          <input
            type="date"
            value={formData.startDate || ''}
            onChange={(e) => onChange({ ...formData, startDate: e.target.value || undefined })}
            className="input w-full"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
          <input
            type="date"
            value={formData.endDate || ''}
            onChange={(e) => onChange({ ...formData, endDate: e.target.value || undefined })}
            className="input w-full"
          />
        </div>
      </div>
    </div>
  );
}
